import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  ChevronRight,
  CircleHelp,
  Clock,
  CreditCard,
  Lock,
  LogOut,
  PencilLine,
  Shield,
  Trash2,
  User,
  UserCog,
} from "lucide-react";
import { logoutCompany } from "../../../Redux/companySlice";
import { clearUserState } from "../../../Redux/userSlice";
import CompanySubscriptionBilling from "./CompanySubscriptionBilling";

const SECTIONS = [
  { id: "account", label: "Account & Profile", icon: User, upcoming: false },
  { id: "subscription", label: "Manage Subscription", icon: CreditCard, upcoming: false },
  { id: "security", label: "Security & Privacy", icon: Shield, upcoming: true },
  { id: "support", label: "Help & Support", icon: CircleHelp, upcoming: true },
  { id: "notifications", label: "Notification & Alert", icon: Bell, upcoming: true },
  { id: "management", label: "Account Management", icon: UserCog, upcoming: false },
];

const formatLabel = (value) => {
  if (!value) return "Not specified";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatPhoneNumber = (phone) => {
  if (!phone) return "Not specified";
  const cleaned = String(phone).replace(/\D/g, "");
  if (cleaned.length < 10) return phone;
  if (String(phone).trim().startsWith("+")) {
    return `+${cleaned.slice(0, cleaned.length - 10)} ${cleaned.slice(-10, -5)} ${cleaned.slice(-5)}`;
  }
  return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
};

const getDisplayName = (company) =>
  company?.companyName ||
  [company?.firstName, company?.lastName].filter(Boolean).join(" ") ||
  company?.email ||
  "Company";

const getAccountBadge = (company) => {
  const type = String(company?.companyAccountType || "").toLowerCase();
  if (type === "brand" || type === "brands") return "Brand Account";
  if (type === "agency" || type === "agencies") return "Agency Account";
  return "Company Account";
};

const SettingsContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const companyState = useSelector((state) => state.company.company);
  const company = companyState?.company || companyState;
  const [activeSection, setActiveSection] = useState("account");
  const [mobileSection, setMobileSection] = useState(null);
  const [profileImageCandidateIndex, setProfileImageCandidateIndex] = useState(0);
  const [showDeleteDevModal, setShowDeleteDevModal] = useState(false);

  const profileImageCandidates = useMemo(() => {
    const uniqueCandidates = new Set();
    [company?.profileImage?.url].forEach((candidate) => {
      if (typeof candidate === "string" && candidate.trim().length > 0) {
        uniqueCandidates.add(candidate);
      }
    });
    return Array.from(uniqueCandidates);
  }, [company?.profileImage?.url]);

  useEffect(() => {
    setProfileImageCandidateIndex(0);
  }, [company?._id, profileImageCandidates]);

  const displayName = getDisplayName(company);
  const activeProfileImageUrl = profileImageCandidates[profileImageCandidateIndex];
  const accountBadge = getAccountBadge(company);
  const locationLabel = company?.location || "Not specified";

  const handleLogout = async () => {
    try {
      await dispatch(logoutCompany()).unwrap();
    } catch (error) {
      console.error("Logout failed:", error);
    }
    dispatch(clearUserState());
    navigate("/");
  };

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case "account":
        return (
          <AccountProfileTab
            company={company}
            displayName={displayName}
            accountBadge={accountBadge}
            locationLabel={locationLabel}
            activeProfileImageUrl={activeProfileImageUrl}
            onImageError={() => setProfileImageCandidateIndex((prev) => prev + 1)}
            onEditProfile={() => navigate("/company/edit-profile")}
          />
        );
      case "subscription":
        return (
          <div className="rounded-2xl border border-gray-200 bg-white p-4 lg:p-6">
            <CompanySubscriptionBilling />
          </div>
        );
      case "security":
        return (
          <UpcomingTab
            title="Security & Privacy"
            description="Password, login protection, and privacy controls will be available here soon."
          />
        );
      case "support":
        return (
          <UpcomingTab
            title="Help & Support"
            description="FAQs, ticket support, and help resources will be available here soon."
          />
        );
      case "notifications":
        return (
          <UpcomingTab
            title="Notification & Alert"
            description="Email, campaign, and billing notification preferences will be available here soon."
          />
        );
      case "management":
        return (
          <AccountManagementTab onDeleteAccount={() => setShowDeleteDevModal(true)} />
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="md:hidden">
        {mobileSection ? (
          <div>
            <button
              type="button"
              onClick={() => setMobileSection(null)}
              className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              Back to Settings
            </button>
            {renderSectionContent(mobileSection)}
          </div>
        ) : (
          <MobileSettingsList onSelect={setMobileSection} onLogout={handleLogout} />
        )}
      </div>

      <div className="hidden md:block">
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Brand Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your profile, billing, notifications, security, and support preferences
          </p>
        </div>

        <div className="mb-6 overflow-x-auto">
          <div className="flex min-w-max gap-1 border-b border-gray-200">
            {SECTIONS.map(({ id, label, icon: Icon }) => {
              const isActive = activeSection === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveSection(id)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap px-3 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? "border-b-2 border-orange-500 text-orange-600"
                      : "border-b-2 border-transparent text-gray-500 hover:text-gray-800"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {renderSectionContent(activeSection)}
      </div>

      {showDeleteDevModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowDeleteDevModal(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-labelledby="delete-dev-title"
          >
            <h3 id="delete-dev-title" className="text-lg font-semibold text-gray-900">
              Feature Under Development
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Account deletion for brand accounts is coming soon. Your data remains secure until then.
            </p>
            <button
              type="button"
              onClick={() => setShowDeleteDevModal(false)}
              className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 md:w-auto"
            >
              Got it
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const MobileSettingsList = ({ onSelect, onLogout }) => (
  <div>
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-gray-900">Brand Settings</h1>
      <p className="mt-1 text-sm text-gray-500">
        Manage your profile, billing, notifications, security, and support preferences
      </p>
    </div>

    <div className="space-y-1">
      {SECTIONS.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => onSelect(id)}
          className="flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left transition-colors hover:bg-gray-50"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
            <Icon className="h-4 w-4" />
          </span>
          <span className="flex-1 text-sm font-medium text-gray-800">{label}</span>
          <ChevronRight className="h-4 w-4 text-gray-400" />
        </button>
      ))}
    </div>

    <div className="mt-4 border-t border-gray-200 pt-2">
      <button
        type="button"
        onClick={onLogout}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-red-500 transition-colors hover:bg-red-50"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50">
          <LogOut className="h-5 w-5" />
        </span>
        <span className="flex-1 text-sm font-medium">Logout</span>
      </button>
    </div>
  </div>
);

const AccountProfileTab = ({
  company,
  displayName,
  accountBadge,
  locationLabel,
  activeProfileImageUrl,
  onImageError,
  onEditProfile,
}) => {
  const contactName =
    [company?.firstName, company?.lastName].filter(Boolean).join(" ") || "Not specified";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-xl font-bold text-gray-700 md:h-16 md:w-16 md:text-2xl">
            {activeProfileImageUrl ? (
              <img
                src={activeProfileImageUrl}
                alt="Profile"
                className="h-full w-full object-cover"
                onError={onImageError}
              />
            ) : (
              displayName.charAt(0).toUpperCase()
            )}
          </div>
          <div className="min-w-0">
            <div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-2">
              <h2 className="truncate text-lg font-semibold text-gray-900 md:text-xl">{displayName}</h2>
              <span className="inline-block rounded-lg border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-600">
                {accountBadge}
              </span>
            </div>
            <p className="mt-1 truncate text-sm text-gray-500">
              {[company?.email, locationLabel !== "Not specified" ? locationLabel : null]
                .filter(Boolean)
                .join("  ·  ")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onEditProfile}
          className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-orange-500 px-2 py-2 text-xs font-medium text-white transition-colors hover:bg-orange-600 md:w-auto md:px-4 md:py-2.5 md:text-sm"
        >
          <PencilLine className="h-4 w-4" />
          Edit profile
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <h3 className="text-base font-semibold text-gray-900">Account Details</h3>
          <p className="mt-1 mb-5 text-sm text-gray-500">
            Update your brand profile and contact information.
          </p>
          <div className="space-y-4">
            <ReadOnlyField label="Company Name" value={displayName} />
            <div>
              <ReadOnlyField label="Email Address" value={company?.email || "Not specified"} locked />
              <p className="mt-1.5 text-xs text-gray-400">
                Your login email is unique to this account and cannot be changed.
              </p>
            </div>
            <ReadOnlyField label="Phone Number" value={formatPhoneNumber(company?.phone)} />
            <ReadOnlyField label="Website" value={company?.companyWebsite || "Not specified"} />
            <ReadOnlyField label="Contact Name" value={contactName} />
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <h3 className="text-base font-semibold text-gray-900">Company & Regional Details</h3>
          <p className="mt-1 mb-5 text-sm text-gray-500">
            Keep your company and location details current.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ReadOnlyField label="Account Type" value={formatLabel(company?.companyAccountType)} />
            <ReadOnlyField label="Industry" value={formatLabel(company?.industryType)} />
            <ReadOnlyField label="Company Stage" value={formatLabel(company?.companyStage)} />
            <ReadOnlyField label="Company Size" value={company?.companySize || "Not specified"} />
            <ReadOnlyField label="Location" value={locationLabel} />
            <ReadOnlyField
              label="Verification Status"
              value={company?.isVerified ? "Verified" : "Pending Verification"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ReadOnlyField = ({ label, value, locked = false }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-gray-700">{label}</span>
    <div className="relative">
      <input
        type="text"
        value={value}
        readOnly
        className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 outline-none"
      />
      {locked ? (
        <Lock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      ) : null}
    </div>
  </label>
);

const UpcomingTab = ({ title, description }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-50">
      <Clock className="h-6 w-6 text-orange-500" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">{description}</p>
    <span className="mt-4 inline-block rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-600">
      Upcoming
    </span>
  </div>
);

const AccountManagementTab = ({ onDeleteAccount }) => (
  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
    <div className="rounded-2xl border border-gray-200 bg-white p-6">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
          <Trash2 className="h-5 w-5 text-red-500" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Delete Account</h3>
          <p className="mt-1 text-sm text-gray-500">
            Permanently remove your Infuber brand account and associated data.
          </p>
        </div>
      </div>

      <ul className="mb-6 space-y-3 text-sm text-gray-600">
        <li className="flex items-start gap-2">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
          Your brands, campaigns, pipelines, and affiliate data will be removed.
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
          You will lose access to billing, social accounts, and team members.
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
          This action cannot be undone.
        </li>
      </ul>

      <button
        type="button"
        onClick={onDeleteAccount}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange-600 md:w-auto"
      >
        <Trash2 className="h-4 w-4" />
        Delete Account
      </button>
    </div>

    <div className="space-y-6">
      <div className="rounded-2xl border border-red-100 bg-red-50 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-500" />
              <h3 className="text-lg font-semibold text-red-700">Caution!</h3>
            </div>
            <p className="mb-3 text-sm text-red-700">
              Account deletion is permanent. Once removed, the following data cannot be recovered:
            </p>
            <ul className="space-y-2 text-sm text-red-700">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                Brands, campaigns, and pipeline history
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                Affiliate records and performance data
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                Billing and subscription records
              </li>
            </ul>
          </div>
          <div className="hidden h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-red-100 sm:flex">
            <Lock className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-50">
            <Shield className="h-5 w-5 text-orange-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900">We take data security seriously</h3>
            <p className="mt-1 text-sm text-gray-500">
              Your data is encrypted and stored securely.
            </p>
            <a
              href="/privacy policy"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-orange-600 hover:text-orange-700"
            >
              Learn more about our policies
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default SettingsContent;
